import { Box, Paper, Radio, RadioGroup, Theme, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Pill } from '@scoir/pills'

import { CadenceTypes, EmailFrequency } from 'store/user'

const CADENCES: {
  value: CadenceTypes
  title: string
  description: string
  pillText?: string
  pillColor?: 'success' | 'info'
}[] = [
  {
    value: EmailFrequency.Immediately,
    title: 'Real-time alerts',
    description: 'Get notified the moment a visit is posted from a target school.',
    pillText: 'Recommended',
    pillColor: 'success',
  },
  {
    value: EmailFrequency.Daily,
    title: 'Daily digest',
    description: 'Get a morning summary of new visits from your target schools.',
  },
  {
    value: EmailFrequency.Weekly,
    title: 'Weekly digest',
    description: 'Get a Monday morning summary of new visits from your target schools.',
    pillText: 'Best for large territories',
    pillColor: 'info',
  },
]

const useStyles = makeStyles((theme: Theme) => ({
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(1),
    border: `1px solid rgba(0, 125, 170, 0.50)`,
    borderRadius: 4,
    cursor: 'pointer',
    transition: 'border-color 120ms, background-color 120ms',
    '&:hover': {
      borderColor: theme.palette.primary.light,
    },
  },
  cardDisabled: {
    cursor: 'not-allowed',
    pointerEvents: 'none',
    opacity: 0.5,
    backgroundColor: theme.palette.grey[100],
  },
  cardSelected: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    background: theme.palette.primary.light + '14',
  },
  radio: {
    display: 'flex',
    padding: 0,
    marginRight: theme.spacing(1.5),
    alignSelf: 'center',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  description: {
    marginTop: theme.spacing(0.5),
  },
  pillContainer: {
    alignSelf: 'center',
  },
}))

const VisitsNotifications = ({
  disabled,
  selectedCadence,
  onCadenceChange,
}: {
  disabled: boolean
  selectedCadence: CadenceTypes
  onCadenceChange: (newPreference: CadenceTypes) => void
}) => {
  const classes = useStyles()

  return (
    <Box>
      <RadioGroup
        className={classes.group}
        value={selectedCadence}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCadenceChange(e.target.value as CadenceTypes)}
      >
        {CADENCES.map((opt) => {
          const selected = selectedCadence === opt.value
          return (
            <Paper
              key={opt.value}
              elevation={0}
              className={`${classes.card} ${selected ? classes.cardSelected : ''} ${
                disabled ? classes.cardDisabled : ''
              }`}
              onClick={() => !disabled && onCadenceChange(opt.value as CadenceTypes)}
            >
              <Radio
                className={classes.radio}
                checked={selected}
                value={opt.value}
                color="primary"
                disabled={disabled}
              />
              <Box flex={1}>
                <Typography variant="body1">{opt.title}</Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.description}
                >
                  {opt.description}
                </Typography>
              </Box>
              {opt.pillText && (
                <Box className={classes.pillContainer}>
                  <Pill color={opt.pillColor}>{opt.pillText}</Pill>
                </Box>
              )}
            </Paper>
          )
        })}
      </RadioGroup>
    </Box>
  )
}

export default VisitsNotifications
